import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkSubmissionModal = ({ isOpen, onClose, onSubmit }) => {
  const [uploadMethod, setUploadMethod] = useState('csv');
  const [csvData, setCsvData] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);

  const csvTemplate = `caller_id,duration,state,zip_code,age,gender,traffic_source,quality_score,notes
(555) 123-4567,120,CA,90210,35,male,search,8,"High quality lead" (555) 987-6543,95,TX,75201,42,female,tv,7,"Good conversion potential" (555) 456-7890,180,FL,33101,28,male,social,6,"Young demographic"`;

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFile(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFile(e?.target?.files?.[0]);
    }
  };

  const handleFile = (file) => {
    if (file?.type !== 'text/csv' && !file?.name?.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }
    
    setUploadedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e?.target?.result;
      setCsvData(text);
      parseAndValidateCSV(text);
    };
    reader?.readAsText(file);
  };

  const parseAndValidateCSV = (csvText) => {
    const lines = csvText?.trim()?.split('\n');
    const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.replace(/"/g, ''));
    const data = [];
    const errors = [];

    const requiredFields = ['caller_id', 'duration', 'state', 'zip_code', 'age', 'traffic_source'];
    
    // Check headers
    const missingHeaders = requiredFields?.filter(field => !headers?.includes(field));
    if (missingHeaders?.length > 0) {
      errors?.push(`Missing required columns: ${missingHeaders?.join(', ')}`);
    }

    // Parse data rows
    for (let i = 1; i < lines?.length; i++) {
      const values = lines?.[i]?.split(',')?.map(v => v?.trim()?.replace(/"/g, ''));
      const row = {};
      
      headers?.forEach((header, index) => {
        row[header] = values?.[index] || '';
      });

      // Validate row
      const rowErrors = [];
      if (!row?.caller_id) rowErrors?.push('Missing caller_id');
      if (!row?.duration || isNaN(row?.duration)) rowErrors?.push('Invalid duration');
      if (!row?.state || row?.state?.length !== 2) rowErrors?.push('Invalid state');
      if (!row?.zip_code || !/^\d{5}(-\d{4})?$/?.test(row?.zip_code)) rowErrors?.push('Invalid zip_code');
      if (!row?.age || isNaN(row?.age) || row?.age < 18 || row?.age > 100) rowErrors?.push('Invalid age');
      if (!row?.traffic_source) rowErrors?.push('Missing traffic_source');

      if (rowErrors?.length > 0) {
        errors?.push(`Row ${i}: ${rowErrors?.join(', ')}`);
      }

      row.rowNumber = i;
      row.isValid = rowErrors?.length === 0;
      data?.push(row);
    }

    setPreviewData(data);
    setValidationErrors(errors);
  };

  const handleTextareaChange = (e) => {
    const text = e?.target?.value;
    setCsvData(text);
    if (text?.trim()) {
      parseAndValidateCSV(text);
    } else {
      setPreviewData([]);
      setValidationErrors([]);
    }
  };

  const handleSubmit = () => {
    if (validationErrors?.length > 0) {
      alert('Please fix validation errors before submitting');
      return;
    }

    const validData = previewData?.filter(row => row?.isValid);
    if (validData?.length === 0) {
      alert('No valid data to submit');
      return;
    }

    onSubmit(validData);
    handleClose();
  };

  const handleClose = () => {
    setCsvData('');
    setUploadedFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setUploadMethod('csv');
    onClose();
  };

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'call_submission_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1100 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Upload" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Bulk Call Submission</h2>
              <p className="text-sm text-muted-foreground">Upload multiple calls at once</p>
            </div>
          </div>
          
          <button
            onClick={handleClose}
            className="p-2 text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Upload Method Selection */}
          <div className="flex items-center space-x-4 mb-6">
            <button
              onClick={() => setUploadMethod('csv')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-smooth ${
                uploadMethod === 'csv' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="FileText" size={16} />
              <span>CSV Upload</span>
            </button>
            
            <button
              onClick={() => setUploadMethod('manual')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-smooth ${
                uploadMethod === 'manual' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Edit" size={16} />
              <span>Manual Entry</span>
            </button>
          </div>

          {uploadMethod === 'csv' && (
            <div className="space-y-6">
              {/* Template Download */}
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-foreground">CSV Template</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadTemplate}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download Template
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Download the CSV template with required columns and example data
                </p>
              </div>

              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-smooth ${
                  dragActive
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  Drop your CSV file here
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  or click to browse and select a file
                </p>
                
                <Button
                  variant="outline"
                  onClick={() => fileInputRef?.current?.click()}
                  iconName="FolderOpen"
                  iconPosition="left"
                >
                  Choose File
                </Button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileInput}
                  className="hidden"
                />
                
                {uploadedFile && (
                  <div className="mt-4 p-3 bg-success/10 rounded-lg">
                    <div className="flex items-center justify-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <span className="text-sm text-success">
                        {uploadedFile?.name} uploaded successfully
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Manual CSV Input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Or paste CSV data directly
                </label>
                <textarea
                  value={csvData}
                  onChange={handleTextareaChange}
                  placeholder="Paste your CSV data here..."
                  className="w-full h-32 px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none font-mono text-sm"
                />
              </div>
            </div>
          )}

          {uploadMethod === 'manual' && (
            <div className="bg-muted/30 rounded-lg p-6 text-center">
              <Icon name="Construction" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Manual Entry</h3>
              <p className="text-sm text-muted-foreground">
                Manual bulk entry interface coming soon. Use CSV upload for now.
              </p>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors?.length > 0 && (
            <div className="mt-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="AlertTriangle" size={16} className="text-error" />
                <h3 className="font-medium text-error">Validation Errors</h3>
              </div>
              <ul className="text-sm text-error space-y-1">
                {validationErrors?.slice(0, 10)?.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
                {validationErrors?.length > 10 && (
                  <li className="text-muted-foreground">
                    ... and {validationErrors?.length - 10} more errors
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Data Preview */}
          {previewData?.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-foreground">Data Preview</h3>
                <div className="text-sm text-muted-foreground">
                  {previewData?.filter(row => row?.isValid)?.length} valid of {previewData?.length} rows
                </div>
              </div>
              
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left text-foreground">Status</th>
                        <th className="px-3 py-2 text-left text-foreground">Caller ID</th>
                        <th className="px-3 py-2 text-left text-foreground">Duration</th>
                        <th className="px-3 py-2 text-left text-foreground">State</th>
                        <th className="px-3 py-2 text-left text-foreground">ZIP</th>
                        <th className="px-3 py-2 text-left text-foreground">Age</th>
                        <th className="px-3 py-2 text-left text-foreground">Source</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {previewData?.slice(0, 20)?.map((row, index) => (
                        <tr key={index} className={row?.isValid ? '' : 'bg-error/5'}>
                          <td className="px-3 py-2">
                            {row?.isValid ? (
                              <Icon name="CheckCircle" size={16} className="text-success" />
                            ) : (
                              <Icon name="XCircle" size={16} className="text-error" />
                            )}
                          </td>
                          <td className="px-3 py-2 text-foreground">{row?.caller_id}</td>
                          <td className="px-3 py-2 text-foreground">{row?.duration}</td>
                          <td className="px-3 py-2 text-foreground">{row?.state}</td>
                          <td className="px-3 py-2 text-foreground">{row?.zip_code}</td>
                          <td className="px-3 py-2 text-foreground">{row?.age}</td>
                          <td className="px-3 py-2 text-foreground">{row?.traffic_source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {previewData?.length > 20 && (
                  <div className="p-3 bg-muted/30 text-center text-sm text-muted-foreground">
                    Showing first 20 rows of {previewData?.length} total rows
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {previewData?.length > 0 && (
              <>
                Ready to submit {previewData?.filter(row => row?.isValid)?.length} valid calls
              </>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            
            <Button
              onClick={handleSubmit}
              disabled={previewData?.filter(row => row?.isValid)?.length === 0}
              iconName="Send"
              iconPosition="right"
            >
              Submit Calls
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSubmissionModal;